import { useEffect } from 'react';
import { useConsent } from '../context/ConsentContext';

/**
 * Loads gtag.js + initialises GA4 — but ONLY when:
 *   1. the visitor has granted analytics consent, AND
 *   2. process.env.REACT_APP_GA4_MEASUREMENT_ID is set (e.g. "G-XXXXXXXX").
 *
 * Until both conditions are met, this hook is a no-op. Safe to mount in App.js
 * even before the env var is configured.
 */
const useGA4 = () => {
  const { choices, decided } = useConsent();

  useEffect(() => {
    if (!decided) return;
    const measurementId = (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();
    if (!measurementId) return;
    if (!choices.analytics) return;
    if (window.__ga4Loaded) return; // idempotent
    window.__ga4Loaded = true;

    // Inject gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.setAttribute('data-testid', 'ga4-script');
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('consent', 'default', {
      ad_storage: choices.marketing ? 'granted' : 'denied',
      ad_user_data: choices.marketing ? 'granted' : 'denied',
      ad_personalization: choices.marketing ? 'granted' : 'denied',
      analytics_storage: 'granted',
    });
    gtag('config', measurementId, { anonymize_ip: true });
  }, [decided, choices.analytics, choices.marketing]);
};

export default useGA4;
